import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, DollarSign, ChefHat, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LocationSwitcher from "@/components/LocationSwitcher";
import DailyRevenueTracker from "@/components/operations/DailyRevenueTracker";
import MenuEngineering from "@/components/operations/MenuEngineering";
import WasteLog from "@/components/operations/WasteLog";

type OpsTab = "daily-revenue" | "menu" | "waste";

const TAB_LABELS: Record<OpsTab, { label: string; icon: React.ElementType }> = {
  "daily-revenue": { label: "Daily Revenue", icon: DollarSign },
  menu: { label: "Menu Engineering", icon: ChefHat },
  waste: { label: "Waste Log", icon: Trash2 },
};

export default function Operations() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const initial = (params.get("tab") as OpsTab) ?? "daily-revenue";
  const [tab, setTab] = useState<OpsTab>(
    initial in TAB_LABELS ? initial : "daily-revenue",
  );

  const setActive = (v: string) => {
    const next = (v as OpsTab);
    setTab(next);
    setParams({ tab: next }, { replace: true });
  };

  return (
    <div className="min-h-screen bg-background pb-16">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-lg font-medium text-foreground">Operations</h1>
            <p className="text-sm text-muted-foreground truncate">Daily revenue, menu profitability, and waste tracking</p>
          </div>
          <LocationSwitcher />
        </div>
      </div>

      <div className="container pt-6 max-w-5xl">
        <Tabs value={tab} onValueChange={setActive} className="mb-6">
          <TabsList className="w-full sm:w-auto overflow-x-auto">
            {(Object.keys(TAB_LABELS) as OpsTab[]).map(key => {
              const Icon = TAB_LABELS[key].icon;
              return (
                <TabsTrigger key={key} value={key} className="gap-1.5">
                  <Icon className="w-3.5 h-3.5" /> {TAB_LABELS[key].label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>

        {tab === "daily-revenue" && <DailyRevenueTracker />}
        {tab === "menu" && <MenuEngineering />}
        {tab === "waste" && <WasteLog />}
      </div>
    </div>
  );
}